import { css } from "@emotion/react";
import React from "react";
import { SearchFilterModal } from "./search-filter-modal";
import { AutoComplete, Button, Input, InputRef, Spin } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { SearchRenderTags } from "./search-render-tags";
import { SearchRenderLabelOption } from "./search-render-label-option";
import { SearchCustomMetric, TSearchCustomMetricTemplateProps } from "./search-custom-metric";
import { SmlAsset } from "@src/types/intelligence-search";
import { FormInstance } from "antd/lib";
import { TSearchFilterFormValueProps } from "@src/util/intelligence-search/render-operator-input-with-form";

export type TSearchValueDisabled = {
    reason: string;
    status: boolean;
};
export type TSearchValue = {
    name?: string;
    meta: SmlAsset;
    isCustomMetric?: boolean;
    disabled?: TSearchValueDisabled;
    showClear?: boolean;
    filter?: {
        name: string;
        value: any;
        operator: string;
    };
};

export type TSearchOption = {
    label: string;
    value: string;
    meta: SmlAsset & TItemDisabledInfo;
};

export type TModalFilterState = {
    open: boolean;
    meta?: SmlAsset;
    name: string;
    formInstance?: FormInstance;
};
export type TModalCustomMetricState = {
    open: boolean;
    formula: string;
    alias: string;
    meta?: SmlAsset;
    formInstance?: FormInstance;
};

export type TItemDisabledInfo = {
    isCustomMetric: boolean;
    disabledFull: boolean;
    disabledLabelOnly: boolean;
    disabledFilterOnly: boolean;
    hasDisabled: boolean;
    unlikelyReasons?: {
        message: string;
        error_type: string;
        incompatible_asset_name: string;
    }[];
};
export type InternalControlSearchProps = {
    handleRemove?: (value: TSearchValue, type: "normal" | "filter") => void;
    handleOpenFilterModal?: (itemName: string, meta: SmlAsset) => void;
    handleOpenCustomMetricModal?: (meta: SmlAsset, formula: string, alias: string) => void;
};
type SearchTemplateProps = {
    formInstance?: FormInstance;
    value?: TSearchValue[];
    onChange?: (value: TSearchValue[]) => void;
    loading?: boolean;
    options: TSearchOption[];
    renderTags?: (values: TSearchValue[], control: InternalControlSearchProps) => React.ReactNode;
    renderLabelOption?: (option: TSearchOption, control: InternalControlSearchProps) => React.ReactNode;
    searchModalFilterValueProps?: TSearchFilterFormValueProps;
    addNewOptionProps?: TSearchCustomMetricTemplateProps;
    onSearch?: (searchText: string, option: TSearchOption) => boolean;
};
export const SearchTemplate: React.FC<SearchTemplateProps> = ({
    formInstance,
    value,
    onChange,
    loading,
    options,
    renderTags,
    renderLabelOption,
    addNewOptionProps,
    onSearch,
    searchModalFilterValueProps,
}) => {
    const values = value || [];

    const [search, setSearch] = React.useState("");
    const [openFilterModalState, setOpenFilterModal] = React.useState<TModalFilterState>();
    const [openCustomMetricModalState, setOpenCustomMetricModal] = React.useState<TModalCustomMetricState>({
        open: false,
        formula: "",
        alias: "",
        formInstance,
    });
    const handleOpenCustomMetricModal = (meta: SmlAsset | undefined, formula: string, alias: string) => {
        setOpenCustomMetricModal({
            open: true,
            meta,
            formula,
            alias,
            formInstance,
        });
        setIsAutoCompleteOpen(false);
    };
    const handleCloseCustomMetricModal = () => {
        setOpenCustomMetricModal({
            open: false,
            formula: "",
            alias: "",
            formInstance,
        });
    };
    const [isAutoCompleteOpen, setIsAutoCompleteOpen] = React.useState(false);
    const inputSearchRef = React.useRef<InputRef>(null);

    const onSelect = (_: string, option: TSearchOption) => {
        const _values = values || [];
        const exitValue = _values?.find((v) => v.meta.name === option.meta.name);
        if (exitValue) {
            onChange?.(_values?.map((v) => (v.meta.name === option.meta.name ? { ...v, name: option.meta.name } : v)));
        } else {
            onChange?.([
                ..._values,
                {
                    name: option.meta.name,
                    meta: option.meta,
                },
            ]);
        }
    };

    const handleRemove = (value: TSearchValue, type: "normal" | "filter") => {
        const _values = values || [];
        const terminateValue = (value: TSearchValue) => {
            onChange?.(_values.filter((v) => v.meta.name !== value.meta.name));
        };
        const terminatePartOfValue = (value: TSearchValue, deleteField: "name" | "filter") => {
            if (deleteField === "name") {
                onChange?.(
                    _values.map((v) =>
                        v.name === value.name
                            ? {
                                  ...v,
                                  name: undefined,
                              }
                            : v,
                    ),
                );
            } else {
                onChange?.(
                    _values.map((v) =>
                        v.name === value.name
                            ? {
                                  ...v,
                                  filter: undefined,
                              }
                            : v,
                    ),
                );
            }
        };
        if (type === "normal") {
            if (value.filter) {
                terminatePartOfValue(value, "name");
            } else {
                terminateValue(value);
            }
        } else {
            if (value.name) {
                terminatePartOfValue(value, "filter");
            } else {
                terminateValue(value);
            }
        }
    };
    // filter modal
    const handleOpenFilterModal = (itemName: string, meta: SmlAsset) => {
        const isCustomMetric = values.find((v) => v.meta.name === itemName)?.isCustomMetric;
        if (isCustomMetric) {
            return;
        }
        setOpenFilterModal({
            open: true,
            meta,
            name: itemName,
            formInstance,
        });
    };
    const handleCloseFilterModal = () => {
        setOpenFilterModal({
            open: false,
            name: "",
            formInstance,
        });
    };

    const handleComfirmFilterModal = (meta: SmlAsset, filter: TSearchValue["filter"]) => {
        const _values = values || [];
        const exitValue = _values.find((v) => v.meta.name === meta.name);
        if (exitValue) {
            onChange?.(
                _values.map((v) =>
                    v.meta.name === meta.name
                        ? {
                              ...v,
                              filter: filter,
                          }
                        : v,
                ),
            );
        } else {
            onChange?.([
                ..._values,
                {
                    meta,
                    filter,
                },
            ]);
        }
        handleCloseFilterModal();
    };

    return (
        <div
            css={css`
                .ant-select-item {
                    height: 50px !important;
                }
            `}
            className="flex flex-wrap items-center gap-1 flex-1 min-w-0 p-2"
        >
            <AutoComplete
                css={css`
                    .ant-select-item {
                        padding: 0px !important;
                    }
                `}
                value={search}
                options={options.filter((o) => {
                    if (onSearch) {
                        return onSearch(search, o);
                    }
                    return (
                        o.meta?.name?.toLowerCase().includes(search.toLowerCase()) ||
                        o.meta?.title?.toLowerCase().includes(search.toLowerCase())
                    );
                })}
                open={isAutoCompleteOpen}
                onOpenChange={setIsAutoCompleteOpen}
                optionRender={(oriOption) => {
                    return (
                        <SearchRenderLabelOption
                            searchValue={search}
                            option={oriOption.data}
                            control={{
                                handleOpenFilterModal,
                            }}
                        />
                    );
                }}
                // optionRender={(oriOption) => {
                //     return renderLabelOption(oriOption.data, {
                //         handleOpenFilterModal,
                //     });
                // }}
                popupRender={(menu) => {
                    return (
                        <Spin spinning={loading}>
                            <div className="">
                                {menu}
                                {addNewOptionProps && (
                                    <div
                                        onKeyDown={(e) => {
                                            if (e.code === "Enter") {
                                                e.stopPropagation();
                                            }
                                        }}
                                        className="flex justify-end my-1"
                                    >
                                        <Button
                                            type="text"
                                            icon={<PlusOutlined />}
                                            onClick={() => {
                                                handleOpenCustomMetricModal?.(undefined, "", "");
                                            }}
                                            className=" text-[12px]"
                                        >
                                            Add Custom Metric
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </Spin>
                    );
                }}
                onSelect={onSelect}
                className="min-w-[300px]"
                getPopupContainer={(props) => props}
                virtual
            >
                <Input
                    ref={inputSearchRef}
                    placeholder="Type to search..."
                    onChange={(e) => setSearch(e.target.value)}
                    onFocus={() => setIsAutoCompleteOpen(true)}
                    suffix={<SearchOutlined style={{ cursor: "pointer" }} />}
                    style={{ minWidth: 120 }}
                />
            </AutoComplete>
            <SearchRenderTags
                values={values}
                control={{
                    handleRemove,
                    handleOpenFilterModal,
                    handleOpenCustomMetricModal,
                }}
            />

            {openFilterModalState && (
                <SearchFilterModal
                    values={values}
                    openFilterModalState={openFilterModalState}
                    handleComfirmFilterModal={handleComfirmFilterModal}
                    handleCloseFilterModal={handleCloseFilterModal}
                    searchModalFilterValueProps={searchModalFilterValueProps}
                />
            )}
            {addNewOptionProps && (
                <SearchCustomMetric
                    {...addNewOptionProps}
                    openCustomMetricModalState={openCustomMetricModalState}
                    handleCloseCustomMetricModal={handleCloseCustomMetricModal}
                />
            )}
        </div>
    );
};
